import React, { useState, useEffect } from 'react';
import { HiOutlinePencilSquare, HiOutlineTrash } from 'react-icons/hi2';
import { FaCog, FaInbox } from 'react-icons/fa';
import { Terminals } from '../../../types';
import { toast } from 'react-toastify';
import {
  WorkstationOption,
  Terminal_TypesOption} from '../utils/utils';
import CreateTerminal from './components/CreateTerminal/CreateTerminal';
import UpdateTerminal from './components/UpdateTerminal/UpdateTerminal';
import DeleteTerminal from './components/DeleteTerminal/DeleteTerminal';
import { updateTerminal, deleteTerminal } from '../../../../../../crud/management_api/terminals';
import './Table.scss';

interface TableProps {
  terminals: Terminals[];
  workstations: WorkstationOption[];
  terminalTypes: Terminal_TypesOption[];
  externalShowSelection: boolean;
  externalSelectedTerminals: string[];
  externalSetSelectedTerminals: React.Dispatch<React.SetStateAction<string[]>>;
  externalShowCreateModal: boolean;
  externalSetShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const Table: React.FC<TableProps> = ({ 
  terminals, 
  workstations, 
  terminalTypes,
  externalShowSelection,
  externalSelectedTerminals,
  externalSetSelectedTerminals,
  externalShowCreateModal,
  externalSetShowCreateModal
}) => {
  const [currentData, setCurrentData] = useState<Terminals[]>(terminals);

  // Actualiza currentData cuando cambian los datos filtrados
  useEffect(() => {
    setCurrentData(terminals);
  }, [terminals]);
  
  // Use external state for selection
  const showSelection = externalShowSelection;
  const selectedTerminals = externalSelectedTerminals;
  const setSelectedTerminals = externalSetSelectedTerminals;
  const showCreateModal = externalShowCreateModal;
  const setShowCreateModal = externalSetShowCreateModal;
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentTerminal, setCurrentTerminal] = useState<Terminals | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleShowDeleteModal = (terminal: Terminals) => {
    setCurrentTerminal(terminal);
    setShowDeleteModal(true);
  };

  const handleShowEditModal = async (terminal: Terminals) => {
    if (terminal) {
      setCurrentTerminal(terminal);
      setShowEditModal(true);
    } else {
      console.error('Failed to fetch terminal for editing.');
      toast.error('El terminal no puede ser editado');
    }
  };

  const toggleItemState = (id: string) => {
    updateTerminal(Number(id), {
      state: !currentData.find(t => t.terminal_id === id)?.state
    })
      .then(() => {
        toast.success('Dispositivo actualizado con éxito');
        setCurrentData(prev =>
          prev.map(t =>
            t.terminal_id === id ? { ...t, state: !t.state } : t
          )
        );
      })
      .catch(error => {
        console.error('Error al actualizar el dispositivo:', error);
        toast.error('Error al actualizar el dispositivo');
      });
  };

  const handleAddTerminal = (newTerminal: Terminals) => {
    setCurrentData(prev => Array.isArray(prev) ? [newTerminal, ...prev] : [newTerminal]);
  };

  const toggleTerminalSelection = (id: string) => {
    setSelectedTerminals(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const renderTable = () => (
    <div className="mp-table-card">
      <table className="mp-table">
        <thead className="mp-table-head">
          <tr>
            {showSelection && <th style={{ width: 40 }} />}
            <th>Nombre</th>
            <th>Estado</th>
            <th>Estación de trabajo</th>
            <th>Tipo</th>
            <th style={{ width: 100, textAlign: 'center' }}>{isMobile ? <FaCog /> : 'Acciones'}</th>
          </tr>
        </thead>
        <tbody className="mp-table-body">
          {currentData.map(term => (
            <tr key={term.terminal_id}>
              {showSelection && (
                <td>
                  <div className="mp-radio-button">
                    <input
                      id={`checkbox-${term.terminal_id}`}
                      type="checkbox"
                      checked={selectedTerminals.includes(term.terminal_id)}
                      onChange={() => toggleTerminalSelection(term.terminal_id)}
                    />
                    <label htmlFor={`checkbox-${term.terminal_id}`} />
                  </div>
                </td>
              )}
              <td className="font-medium">{term.terminal_name}</td>
              <td>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={term.state}
                    onChange={() => toggleItemState(term.terminal_id)}
                  />
                  <span className="slider round" />
                </label>
              </td>
              <td>
                {workstations.find(w => w.id === Number(term.workstation_id))?.name ||
                  'Desconocido'}
              </td>
              <td>
                {terminalTypes.find(t => t.id === Number(term.terminal_type_id))
                  ?.name || 'Desconocido'}
              </td>
              <td>
                <div className="flex items-center justify-center gap-1.5">
                  <button className="mp-btn-icon" onClick={() => handleShowEditModal(term)} title="Editar">
                    <HiOutlinePencilSquare size={16} />
                  </button>
                  <button className="mp-btn-icon mp-btn-icon-danger" onClick={() => handleShowDeleteModal(term)} title="Eliminar">
                    <HiOutlineTrash size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderNoData = () => (
    <div className="mp-table-card" style={{ padding: '3rem', textAlign: 'center' }}>
      <FaInbox size={32} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
      <div style={{ opacity: 0.6, marginBottom: '1rem' }}>No has creado ningún dispositivo aún!</div>
      <button className="mp-btn-primary" onClick={() => setShowCreateModal(true)}>
        Crear dispositivo
      </button>
    </div>
  );

  return (
    <div>
      {currentData.length === 0 ? renderNoData() : renderTable()}

      <DeleteTerminal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        currentTerminal={currentTerminal}
        currentData={currentData}
        setCurrentData={setCurrentData}
      />

      <CreateTerminal
        showCreateModal={showCreateModal}
        setShowCreateModal={setShowCreateModal}
        setCurrentData={handleAddTerminal}
      />

      <UpdateTerminal
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        setCurrentData={setCurrentData}
        currentData={currentData}
        currentTerminal={currentTerminal}
      />
    </div>
  );
};

export default Table;
